import { useQuery } from "@tanstack/react-query";
import { getMyPass, getPassPricing, passKey, passPricingKey, rupees, type PassPricing } from "@/api/service/pass";
import { useAuth } from "@/hooks/useAuth";

/** Whether the signed-in user holds the pass for the current cycle. */
export function usePass() {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: passKey,
    queryFn: getMyPass,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
  const status = query.data?.data ?? null;
  return {
    ...query,
    status,
    active: !!status?.active,
    expiresAt: status?.expires_at ?? null,
    entitlements: status?.entitlements ?? null,
  };
}

const FALLBACK: PassPricing = { amount_paise: 49_900, list_paise: 49_900, offer: null };

/** Current price, signed in or not. Falls back to list price while loading. */
export function usePassPricing() {
  const query = useQuery({
    queryKey: passPricingKey,
    queryFn: getPassPricing,
    staleTime: 10 * 60 * 1000,
  });
  const pricing = query.data?.data ?? FALLBACK;
  const ends = pricing.offer ? Date.parse(pricing.offer.ends_at) : null;
  const daysLeft = ends ? Math.max(0, Math.ceil((ends - Date.now()) / 86_400_000)) : null;
  return {
    pricing,
    price: rupees(pricing.amount_paise),
    listPrice: rupees(pricing.list_paise),
    offer: pricing.offer,
    daysLeft,
    loaded: !!query.data,
  };
}
